/* 折疊面板 */
import { useRef, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTheme, styled } from '@mui/material/styles';
import {
  Backdrop,
  Box,
  Divider,
  ListItem,
  Stack, 
  Typography,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  accordionSummaryClasses,
} from '@mui/material';
import SimPagination from '../pagination';
import { NoData } from '../error';
import { LoadingScreen } from '../loading-screen';
import { SvgPlusFill } from '../SvgIcons';




// ================================|| Catering List Item ||================================ //
const StyledAccordion = styled((props) => (
  <Accordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
  '&:hover': {
    [`.${accordionSummaryClasses.root}`]: {
      backgroundColor: theme.palette.action.hover
    },
  }
}));

const StyledAccordionSummary = styled((props) => (
  <AccordionSummary
    expandIcon={<SvgPlusFill />}
    {...props}
  />
))(({ theme }) => ({
  padding: theme.spacing(3, 1.5),
  '&:before': {
    content: '""',
    display: 'block',
    height: '1px',
    width: '100%',
    position: 'absolute',
    bottom: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& svg': {
    color: theme.palette.common.black,
    fill: theme.palette.common.black,
    transition: 'all .3s ease',
  },
  ...theme.typography.hoverPrimaryLeftline2px,
  [`&.${accordionSummaryClasses.expanded}`]:
    {
      backgroundColor: theme.palette.primary.lighter,
      '&:after': {
        width: '100%'
      }
    },
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: 'rotate(45deg)',
      '& svg': {
        color: `${theme.palette.action.selected}`,
        fill: `${theme.palette.action.selected}`,
      }
    },
  [`& .${accordionSummaryClasses.content}`]: {
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    margin: theme.spacing(0),
  },
  ...theme.applyStyles('dark', {}),
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.grey[300],
  borderBottom: `1px solid ${theme.palette.divider}`,
}));
// ================================|| Service List ||================================ //
const SimAccordionList = ({
  list = [],
  page = 1,
  defaultPage = 1,
  rowsPerPage = 10,
  total = 0,
  count = 0,
  onChangePage,
  onChangeRowsPerPage,
  loading = false,
  itemMinHeight = 72,
  disabledRowsEmpty = false,
  hideHeader = false
})=> {
  const listRef = useRef(null);
  const theme = useTheme();

  const [expanded, setExpanded] = useState('faqs-panel1');

  // 页码跳转
  const onQuickJumper = async (event) => {
    if(event?.target?.value && event?.target?.value > 0 && event?.target?.value <= count) {
      await onChangePage(event, Number(event.target.value));
    }
    if(event?.target?.value && event?.target?.value > 0 && event?.target?.value > count) {
      await onChangePage(event, Number(count));
    }
  };

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const emptyRows = list?.length < Number(rowsPerPage) ? Math.floor(10 - list?.length) : 0;

  useState(() => {
    setExpanded(false);
  }, [list])

  return (
    <Stack spacing={3}>
      {
        (!hideHeader) && (
          <Stack direction={'row'} alignItems={'center'}>
            <Typography variant='h6' minHeight={40} display={'flex'} alignItems={'center'}>
              共&nbsp;
              <Typography variant='h6' color={theme.palette.action.selected} component={'span'}>{total}</Typography> 
              &nbsp;筆相關資料
            </Typography>
          </Stack>
        )
      }
      <Stack>
        <Box
          ref={listRef} 
          position={'relative'} 
          overflow={'hidden'}
          minHeight={(itemMinHeight * Number(list?.length)) > 120 ? (itemMinHeight * Number(list?.length)) : 120}
          sx={{
            transition: 'all 0.26s linear',
            transformOrigin: 'top',
          }}
        >
          <Backdrop
            sx={{
              position: 'absolute',
              textAlign: 'center',
              height: '100%',
              zIndex: theme.zIndex.drawer + 1,
              backgroundColor: 'transparent'
            }}
            open={Boolean(loading)}
          >
            <LoadingScreen sx={{ alignItems: 'flex-start', minHeight: 'auto' }} />
          </Backdrop>

          {
            total > 0 ? (
              <Stack>
                {
                  _.map(list, (item, index) => {
                    return (
                      <StyledAccordion key={index} expanded={expanded === `faqs-panel${index}`} onChange={handleChange(`faqs-panel${index}`)}>
                        <StyledAccordionSummary 
                          aria-controls={`Sim-panel${index}d-content`} 
                          id={`Sim-panel${index}d-header`}
                        >
                          <Typography variant={`h6`} maxWidth={650} noWrap>{item?.title}</Typography>
                        </StyledAccordionSummary>
                        <StyledAccordionDetails>
                          <Typography variant={`h6`} fontWeight={400} dangerouslySetInnerHTML={{ __html: item?.detailsDto?.contentHtml || '--' }} />
                        </StyledAccordionDetails>
                      </StyledAccordion>
                    )
                  })
                }
                {emptyRows > 0 && (!disabledRowsEmpty) && (
                  <Box style={{ height: ((listRef.current?.scrollHeight) / Number(rowsPerPage)) * emptyRows }} />
                )}
              </Stack>
            ) : (
              loading ? (
                _.map(Array.from({ length: Number(rowsPerPage) }), (item, index) => {
                  return (
                    <ListItem key={index} sx={{ mb: 0.5 }} disableGutters>
                      {/* <Skeleton variant="rectangular" width={`100%`} height={itemMinHeight} /> */}
                    </ListItem>)
                })
              ) : (
                <NoData minHeight={300} />
              )
            )
          }
        </Box>
        <SimPagination
          total={Number(total)}
          page={Number(page)}
          defaultPage={Number(defaultPage)}
          rowsPerPage={Number(rowsPerPage)}
          count={Number(count)}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onQuickJumper={onQuickJumper}
        />
      </Stack>
    </Stack>
  )
};
SimAccordionList.propTypes = {
  list: PropTypes.array,
  total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rowsPerPage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  onChangePage: PropTypes.func,
  onChangeRowsPerPage: PropTypes.func,
  limit: PropTypes.number,

  onClick: PropTypes.func,
  loading: PropTypes.bool,
};

export default SimAccordionList
